// Lösch-Optionen-Dialog (nur dieser / dieser + zukünftige) —
// extrahiert aus AppointmentsPage.js.
// 06/2026: Serien-Lösch-Button wird gesperrt solange noch Mindbody-Pushes
// laufen — verhindert Race Condition (Termin würde NACH dem Cancel-Call
// in Mindbody erstellt und bliebe als Geister-Slot stehen).
import React, { useEffect, useState } from 'react';
import { X, Loader2 } from 'lucide-react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const DeleteOptionsDialog = ({ open, onOpenChange, appointment, services, onDelete }) => {
  const [pushStatus, setPushStatus] = useState(null);
  // 06/2026: Standard AN — Admin kann abwählen, um still (ohne WhatsApp/E-Mail) zu stornieren.
  const [notifyCustomer, setNotifyCustomer] = useState(true);
  // 06/2026: optionaler Storno-Grund (erscheint in der Kundenhistorie).
  const [reason, setReason] = useState('');

  // Bei jedem Öffnen zurück auf Standard (benachrichtigen, kein Grund).
  useEffect(() => {
    if (open) { setNotifyCustomer(true); setReason(''); }
  }, [open]);

  // Polling für Serien-Push-Status — sperrt den Serien-Lösch-Button,
  // solange `pending > 0`. Stoppt automatisch wenn `pending == 0`.
  useEffect(() => {
    let interval = null;
    let cancelled = false;
    if (open && appointment?.is_series && appointment?.series_id) {
      const fetchStatus = async () => {
        try {
          const token = localStorage.getItem('token');
          const { data } = await axios.get(
            `${API}/appointments/series/${appointment.series_id}/push-status`,
            { headers: { Authorization: `Bearer ${token}` } }
          );
          if (cancelled) return;
          setPushStatus(data);
          if (data.pending === 0 && interval) {
            clearInterval(interval);
            interval = null;
          }
        } catch {
          if (cancelled) return;
          // Endpoint optional — bei Fehler einfach Button aktiv lassen
          setPushStatus({ pending: 0, total: 0, ready_for_deletion: true });
        }
      };
      fetchStatus();
      interval = setInterval(fetchStatus, 2000);
    }
    return () => {
      cancelled = true;
      if (interval) clearInterval(interval);
    };
  }, [open, appointment?.is_series, appointment?.series_id]);

  const seriesPending = pushStatus && pushStatus.pending > 0;
  const seriesReady = !pushStatus || pushStatus.ready_for_deletion;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Termin löschen</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-gray-600">
            Wie möchten Sie den Termin löschen?
          </p>

          {/* 06/2026: Kunde über Stornierung benachrichtigen (Standard AN) */}
          <label
            className="flex items-start gap-2 p-3 bg-gray-50 border border-gray-200 rounded-md cursor-pointer select-none"
            htmlFor="notify-customer-checkbox"
          >
            <Checkbox
              id="notify-customer-checkbox"
              checked={notifyCustomer}
              onCheckedChange={(v) => setNotifyCustomer(v === true)}
              className="mt-0.5"
              data-testid="notify-customer-checkbox"
            />
            <span className="text-sm text-gray-700">
              Kunde über Stornierung benachrichtigen
              <span className="block text-xs text-gray-500">
                (WhatsApp / E-Mail). Abwählen, um still zu stornieren.
              </span>
            </span>
          </label>

          {/* 06/2026: Optionaler Storno-Grund (Kundenhistorie) */}
          <div className="space-y-1">
            <label htmlFor="cancellation-reason-input" className="text-sm text-gray-700">
              Storno-Grund <span className="text-xs text-gray-500">(optional)</span>
            </label>
            <Input
              id="cancellation-reason-input"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="z.B. krank, Termin verschoben, Zahlungsproblem …"
              maxLength={200}
              data-testid="cancellation-reason-input"
            />
          </div>

          <div className="space-y-2">
            <Button
              variant="outline"
              className="w-full justify-start text-left hover:bg-red-50"
              onClick={() => {
                onDelete('this', notifyCustomer, reason);
                onOpenChange(false);
              }}
              data-testid="delete-this-appointment-btn"
            >
              <X size={16} className="mr-2" />
              Nur diesen Termin löschen
            </Button>

            {appointment?.is_series && (() => {
              const service = services.find(s => s.id === appointment?.service_id);
              const currentParticipants = appointment?.customer_ids?.length || 1;
              const isGroupTraining = service && service.max_participants > 1;

              if (isGroupTraining && currentParticipants > 1) {
                return (
                  <div className="p-3 bg-blue-50 border border-blue-200 rounded-md text-xs text-blue-700">
                    ℹ️ Bei Gruppen-Trainings mit mehreren Teilnehmern: Bitte einzelne Teilnehmer entfernen oder jeden Termin einzeln löschen
                  </div>
                );
              }

              return (
                <>
                  <Button
                    variant="outline"
                    className="w-full justify-start text-left hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-60"
                    disabled={seriesPending}
                    onClick={() => {
                      if (seriesPending) return;
                      if (!window.confirm('Diesen und ALLE zukünftigen Termine dieser Serie löschen?')) return;
                      if (!window.confirm('Bist du WIRKLICH sicher? Alle zukünftigen Termine dieser Serie werden unwiderruflich gelöscht. Dies kann NICHT rückgängig gemacht werden.')) return;
                      onDelete('future', notifyCustomer, reason);
                      onOpenChange(false);
                    }}
                    data-testid="delete-series-appointments-btn"
                  >
                    {seriesPending ? (
                      <Loader2 size={16} className="mr-2 animate-spin text-amber-600" />
                    ) : (
                      <X size={16} className="mr-2" />
                    )}
                    Dieser + alle zukünftigen Termine
                  </Button>

                  {seriesPending && pushStatus && (
                    <div
                      className="p-3 bg-amber-50 border border-amber-200 rounded-md text-xs text-amber-800"
                      data-testid="series-push-pending-info"
                    >
                      ⏳ Mindbody-Sync läuft noch — {pushStatus.pending} von {pushStatus.total} Terminen warten auf Bestätigung.
                      <br />Der &quot;Serie löschen&quot;-Button wird automatisch aktiv, sobald alle Termine synchronisiert sind.
                    </div>
                  )}

                  {seriesReady && pushStatus && pushStatus.total > 0 && (
                    <div
                      className="p-2 text-xs text-emerald-700"
                      data-testid="series-push-ready-info"
                    >
                      ✓ Alle {pushStatus.total} Termine synchronisiert — Serie kann sicher gelöscht werden.
                    </div>
                  )}
                </>
              );
            })()}
          </div>

          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="w-full"
          >
            Abbrechen
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
